import { unifiedClient } from '@/lib/unified-fetch';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

/**
 * Revenue service functions using unified admin client
 */

// Types based on openapi-admin schema
export type RevenueItem = {
  id: number;
  date: string; // Format: date
  type: 'forecast_baseline' | 'monthly_target';
  amount: number;
  createdAt: string; // Format: date-time
  updatedAt: string; // Format: date-time
};

export type RevenueCreateUpdate = {
  id?: number;
  date: string; // Format: date
  type: 'forecast_baseline' | 'monthly_target';
  amount: number;
};

export type RevenueFilter = {
  q?: string;
  id?: number;
  id_lte?: number;
  id_gte?: number;
  id_in?: number[];
  date?: string;
  date_lte?: string;
  date_gte?: string;
  type?: 'forecast_baseline' | 'monthly_target';
  type_has?: string;
  type_in?: ('forecast_baseline' | 'monthly_target')[];
  amount?: number;
  amount_lte?: number;
  amount_gte?: number;
  createdAt_lte?: string;
  createdAt_gte?: string;
  updatedAt_lte?: string;
  updatedAt_gte?: string;
};

/**
 * Fetch baseline forecast revenues with pagination
 */
export const useBaselineForecasts = (
  limit: number = 50,
  offset: number = 0,
  filter?: RevenueFilter,
  sort?: {
    field?: string;
    order?: 'ASC' | 'DESC';
  }
) => {
  return useQuery({
    queryKey: ['baselineForecasts', { limit, offset, filter, sort }],
    queryFn: async (): Promise<RevenueItem[]> => {
      const response = await unifiedClient.admin.GET('/event_Revenue/', {
        params: {
          query: {
            limit,
            offset,
            sort,
            filter: {
              type: 'forecast_baseline' as const,
              ...filter,
            } as any, // Type assertion to allow monthly_target until OpenAPI schema is updated
          },
        },
      });

      const { data, error } = response;

      if (error) {
        throw new Error('Failed to fetch baseline forecasts');
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Infinite query for baseline forecasts (for scrolling)
 */
export const useInfiniteBaselineForecasts = (
  pageSize: number = 50,
  filter?: RevenueFilter,
  sort?: {
    field?: string;
    order?: 'ASC' | 'DESC';
  }
) => {
  return useInfiniteQuery({
    queryKey: ['infiniteBaselineForecasts', { pageSize, filter, sort }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<{
      data: RevenueItem[];
      hasMore: boolean;
    }> => {
      const response = await unifiedClient.admin.GET('/event_Revenue/', {
        params: {
          query: {
            limit: pageSize,
            offset: pageParam * pageSize,
            sort,
            filter: {
              type: 'forecast_baseline' as const,
              ...filter,
            } as any, // Type assertion to allow monthly_target until OpenAPI schema is updated
          },
        },
      });

      const { data, error } = response;

      if (error) {
        throw new Error('Failed to fetch baseline forecasts');
      }

      const items = data || [];
      const hasMore = items.length === pageSize;

      return {
        data: items,
        hasMore,
      };
    },
    getNextPageParam: (lastPage: { data: RevenueItem[]; hasMore: boolean }, pages: any[]) => {
      return lastPage.hasMore ? pages.length : undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mass set/update baseline forecasts
 */
export const useMassSetBaselineForecasts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RevenueCreateUpdate[]): Promise<void> => {
      try {
        await unifiedClient.admin.PUT('/event_Revenue/many', {
          body: data as any, // Type assertion to allow monthly_target until OpenAPI schema is updated
        });
        // If we reach here, the request was successful (status 200)
      } catch (error: any) {
        // Check if it's a JSON parsing error from empty response (which is actually success)
        if (error.message?.includes('JSON') || error.message?.includes('Unexpected end')) {
          // This is likely an empty 200 response, which is success
          return;
        }
        // Re-throw other errors
        throw new Error('Failed to set baseline forecasts');
      }
    },
    onSuccess: () => {
      // Invalidate all baseline forecast queries
      queryClient.invalidateQueries({ queryKey: ['baselineForecasts'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteBaselineForecasts'] });
    },
  });
};

/**
 * Update multiple baseline forecasts
 */
export const useUpdateBaselineForecasts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { 
      ids: number[]; 
      data: Partial<RevenueCreateUpdate> 
    }): Promise<void> => {
      // Convert to the format expected by the many endpoint
      const updateData: RevenueCreateUpdate[] = data.ids.map(id => ({
        id,
        type: 'forecast_baseline' as const,
        date: '', // Will be filled by the actual update data
        amount: 0, // Will be filled by the actual update data
        ...data.data,
      }));

      try {
        await unifiedClient.admin.PUT('/event_Revenue/many', {
          body: updateData as any, // Type assertion to allow monthly_target until OpenAPI schema is updated
        });
        // If we reach here, the request was successful (status 200)
      } catch (error: any) {
        // Check if it's a JSON parsing error from empty response (which is actually success)
        if (error.message?.includes('JSON') || error.message?.includes('Unexpected end')) {
          // This is likely an empty 200 response, which is success
          return;
        }
        // Re-throw other errors
        throw new Error('Failed to update baseline forecasts');
      }
    },
    onSuccess: () => {
      // Invalidate all baseline forecast queries
      queryClient.invalidateQueries({ queryKey: ['baselineForecasts'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteBaselineForecasts'] });
    },
  });
};

/**
 * Delete baseline forecasts
 */
export const useDeleteBaselineForecasts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: number[]): Promise<void> => {
      const response = await unifiedClient.admin.DELETE('/event_Revenue/{ids}', {
        params: {
          path: { ids },
        },
      });

      const { error } = response;

      if (error) {
        throw new Error('Failed to delete baseline forecasts');
      }
    },
    onSuccess: () => {
      // Invalidate all baseline forecast queries
      queryClient.invalidateQueries({ queryKey: ['baselineForecasts'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteBaselineForecasts'] });
    },
  });
};

/**
 * Get specific baseline forecasts by IDs
 */
export const useBaselineForecastsByIds = (ids: number[]) => {
  return useQuery({
    queryKey: ['baselineForecasts', 'byIds', ids],
    queryFn: async (): Promise<RevenueItem[]> => {
      if (ids.length === 0) return [];

      const response = await unifiedClient.admin.GET('/event_Revenue/{ids}', {
        params: {
          path: { ids },
        },
      });

      const { data, error } = response;

      if (error) {
        throw new Error('Failed to fetch baseline forecasts by IDs');
      }

      return data || [];
    },
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Monthly Target specific hooks using the same API with type: 'monthly_target'
 */

/**
 * Fetch monthly targets for a specific year
 */
export const useMonthlyTargets = (year: number) => {
  return useQuery({
    queryKey: ['monthlyTargets', year],
    queryFn: async (): Promise<RevenueItem[]> => {
      const response = await unifiedClient.admin.GET('/event_Revenue/', {
        params: {
          query: {
            limit: 12, // Maximum 12 months
            offset: 0,
            filter: {
              type: 'monthly_target' as any, // Type assertion until OpenAPI schema is updated
              date_gte: `${year}-01-01`,
              date_lte: `${year}-12-31`,
            } as any,
          },
        },
      });

      const { data, error } = response;

      if (error) {
        throw new Error('Failed to fetch monthly targets');
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get monthly target for a specific month/year
 */
export const useMonthlyTarget = (year: number, month: number) => {
  return useQuery({
    queryKey: ['monthlyTarget', year, month],
    queryFn: async (): Promise<RevenueItem | null> => {
      // Create first day of the month as the date key
      const monthStr = month.toString().padStart(2, '0');
      const dateKey = `${year}-${monthStr}-01`;

      const response = await unifiedClient.admin.GET('/event_Revenue/', {
        params: {
          query: {
            limit: 1,
            offset: 0,
            filter: {
              type: 'monthly_target' as any, // Type assertion until OpenAPI schema is updated
              date: dateKey,
            } as any,
          },
        },
      });

      const { data, error } = response;

      if (error) {
        throw new Error('Failed to fetch monthly target');
      }

      const targets = data || [];
      return targets.length > 0 ? targets[0] : null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mass set monthly targets (converts month inputs to first day of each month)
 */
export const useMassSetMonthlyTargets = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targets: Array<{ year: number; month: number; amount: number }>): Promise<void> => {
      // Convert month inputs to date format (first day of each month)
      const data: RevenueCreateUpdate[] = targets.map(target => ({
        date: `${target.year}-${target.month.toString().padStart(2, '0')}-01`,
        type: 'monthly_target' as const,
        amount: target.amount,
      }));

      try {
        await unifiedClient.admin.PUT('/event_Revenue/many', {
          body: data as any, // Type assertion to allow monthly_target until OpenAPI schema is updated
        });
        // If we reach here, the request was successful (status 200)
      } catch (error: any) {
        // Check if it's a JSON parsing error from empty response (which is actually success)
        if (error.message?.includes('JSON') || error.message?.includes('Unexpected end')) {
          // This is likely an empty 200 response, which is success
          return;
        }
        // Re-throw other errors
        throw new Error('Failed to set monthly targets');
      }
    },
    onSuccess: () => {
      // Invalidate monthly target queries
      queryClient.invalidateQueries({ queryKey: ['monthlyTargets'] });
      queryClient.invalidateQueries({ queryKey: ['monthlyTarget'] });
    },
  });
};

/**
 * Update single monthly target
 */
export const useUpdateMonthlyTarget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: number; amount: number; year: number; month: number }): Promise<void> => {
      const updateData: RevenueCreateUpdate[] = [{
        id: data.id,
        date: `${data.year}-${data.month.toString().padStart(2, '0')}-01`,
        type: 'monthly_target' as const,
        amount: data.amount,
      }];

      try {
        await unifiedClient.admin.PUT('/event_Revenue/many', {
          body: updateData as any, // Type assertion to allow monthly_target until OpenAPI schema is updated
        });
        // If we reach here, the request was successful (status 200)
      } catch (error: any) {
        // Check if it's a JSON parsing error from empty response (which is actually success)
        if (error.message?.includes('JSON') || error.message?.includes('Unexpected end')) {
          // This is likely an empty 200 response, which is success
          return;
        }
        // Re-throw other errors
        throw new Error('Failed to update monthly target');
      }
    },
    onSuccess: () => {
      // Invalidate monthly target queries
      queryClient.invalidateQueries({ queryKey: ['monthlyTargets'] });
      queryClient.invalidateQueries({ queryKey: ['monthlyTarget'] });
    },
  });
};

/**
 * Delete monthly targets
 */
export const useDeleteMonthlyTargets = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: number[]): Promise<void> => {
      const response = await unifiedClient.admin.DELETE('/event_Revenue/{ids}', {
        params: {
          path: { ids },
        },
      });

      const { error } = response;

      if (error) {
        throw new Error('Failed to delete monthly targets');
      }
    },
    onSuccess: () => {
      // Invalidate monthly target queries
      queryClient.invalidateQueries({ queryKey: ['monthlyTargets'] });
      queryClient.invalidateQueries({ queryKey: ['monthlyTarget'] });
    },
  });
};
